 

import type { Question, Summary, SummaryMap, QuestionGroup, WindowWithConfig, Config, CorrelationEntry, CorrelationData } from './shared/types';
import { renderSankey } from './shared/sankey';
import { renderWordCloud } from './shared/wordcloud';
import { byId, normalizeNumber, normalizeNumberArray, compareQuestionIds, getBarOrientation } from './shared/utils';
import { renderBarChart } from './shared/bar';
import { renderPieChart, shouldRenderPie } from './shared/pie';
import { renderNumericSummary } from './shared/numeric';
import { renderBoxPlotChartJS } from './shared/boxchart';
import { renderLongform, renderCategorizedLongform } from './shared/longform';
import { renderStackedSummary } from './shared/stacked';
import { GROUP_DEFINITIONS } from './shared/groups';
import { SECTIONS } from './shared/sections';
import { getCommentary } from './shared/commentary';
import { renderCorrelationHeatmap } from './shared/heatmap';

const root = window as WindowWithConfig;
const config: Config = root.ECE25 ?? {};
const RESPONSES_URL = config.responsesUrl ?? '/data/results.json';

 

const slidesEl = byId<HTMLElement>('question-slides');
const statQuestionsEl = byId<HTMLSpanElement>('stat-questions');

let questions: Question[] = [];
let summaries: SummaryMap = {};
let correlationData: CorrelationData = {};
const activeCharts: any[] = [];

const HIDDEN_QUESTION_IDS = new Set<string>(['A1-intramurals', 'A1-varsity']);
 

function inferQuestionType(entry: Summary): string {
  if (entry.values && entry.values.length) {
    return 'numeric';
  }

  if (entry.words && entry.words.length) {
    return 'wordcloud';
  }

  if (entry.labels && entry.counts && entry.labels.length && entry.counts.length) {
    const total = entry.counts.reduce((sum, value) => sum + value, 0);
    if (entry.n && total > entry.n) {
      return 'MS';
    }
    return 'MC';
  }

  if (entry.responses && entry.responses.length) {
    return 'longform';
  }

  return 'freeform';
}

function createPill(text: string, className: string): HTMLSpanElement {
  const pill = document.createElement('span');
  pill.className = `pill ${className}`;
  pill.textContent = text;
  return pill;
}

function wrapWithAside(content: HTMLElement | null, commentaryEl: HTMLElement | null, position: 'left' | 'right'): HTMLElement | null {
  if (!content && !commentaryEl) return null;
  if (!commentaryEl) return content;
  if (!content) return commentaryEl;
  const wrapper = document.createElement('div');
  wrapper.className = `content-aside content-aside--${position}`;
  if (position === 'left') {
    commentaryEl.classList.add('aside');
    const main = document.createElement('div');
    main.className = 'main';
    main.appendChild(content);
    wrapper.appendChild(commentaryEl);
    wrapper.appendChild(main);
  } else {
    const main = document.createElement('div');
    main.className = 'main';
    main.appendChild(content);
    commentaryEl.classList.add('aside');
    wrapper.appendChild(main);
    wrapper.appendChild(commentaryEl);
  }
  return wrapper;
}

// Expand a list of id tokens that may include ranges (e.g., 'A4-A10' or 'A4..A10')
function expandIdTokens(tokens: string[], questionLookup: Map<string, unknown>): string[] {
  const result: string[] = [];
  const seen = new Set<string>();
  const add = (id: string) => { if (!seen.has(id)) { result.push(id); seen.add(id); } };
  const parseId = (value: string) => {
    const m = value.trim().match(/^([A-Za-z]+)(\d+)$/);
    if (!m) return null;
    return { prefix: m[1], num: Number(m[2]) };
  };
  tokens.forEach((raw) => {
    const token = String(raw).trim();
    if (!token) return;
    // If token exactly matches a known question id (including hyphenated ids like A26-used),
    // accept it directly before attempting range parsing.
    if (questionLookup.has(token)) { add(token); return; }
    // Detect range separator
    const sepIdxDash = token.indexOf('-');
    const sepIdxDots = token.indexOf('..');
    let parts: string[] | null = null;
    if (sepIdxDots >= 0) {
      parts = token.split('..');
    } else if (sepIdxDash >= 0) {
      parts = token.split('-');
    }
    if (parts && parts.length === 2) {
      const start = parseId(parts[0]);
      const endRaw = parts[1].trim();
      let end = parseId(endRaw);
      if (!start) return;
      if (!end) {
        // allow end part to be just number
        const m = endRaw.match(/^(\d+)$/);
        if (m) end = { prefix: start.prefix, num: Number(m[1]) } as any;
      }
      if (!end || start.prefix !== end.prefix) return;
      const a = Math.min(start.num, end.num);
      const b = Math.max(start.num, end.num);
      for (let n = a; n <= b; n += 1) {
        const id = `${start.prefix}${n}`;
        if (questionLookup.has(id)) add(id);
      }
      return;
    }
    // Single id
    if (questionLookup.has(token)) add(token);
  });
  return result;
}

function createCommentaryEl(questionId: string, summary: Summary): HTMLElement | null {
  const cfg = getCommentary(questionId);
  const commentaryHtml = cfg?.html;
  const commentaryText = commentaryHtml ? undefined : ((summary as any).commentary as string | undefined);
  const hasCommentary = Boolean(commentaryHtml || commentaryText);
  if (!hasCommentary) return null;
  const el = document.createElement('div');
  el.className = 'commentary';
  if (commentaryHtml) {
    el.innerHTML = commentaryHtml;
  } else if (commentaryText) {
    el.textContent = commentaryText;
  }
  return el;
}

 

function createHighlight(_question: Question, _summary: Summary): HTMLElement | null {
  return null;
}

function destroyCharts(): void {
  const items: any[] = [];
  if (Array.isArray((globalThis as any).__activeCharts)) {
    items.push(...(globalThis as any).__activeCharts);
  }
  if (Array.isArray(activeCharts)) {
    items.push(...activeCharts);
  }
  items.forEach((chart) => {
    if (chart && typeof chart.destroy === 'function') {
      try { chart.destroy(); } catch {}
    }
  });
  if (Array.isArray((globalThis as any).__activeCharts)) {
    (globalThis as any).__activeCharts.length = 0;
  }
  if (Array.isArray(activeCharts)) {
    activeCharts.length = 0;
  }
}

function getResponseNote(question: Question): string {
  if (question.type === 'MS') {
    return 'responses (multi-select)';
  }
  return 'responses';
}

function renderChartContent(
  question: Question,
  summary: Summary,
  chartHint?: string,
  opts?: { hideStats?: boolean }
): HTMLElement | null {
  if (summary.flow || chartHint === 'sankey') {
    return renderSankey(summary, {colorMode: 'to' });
  }

  // Handle correlation type questions with heatmap
  if (question.type === 'correlation' && correlationData[question.id]) {
    return renderCorrelationHeatmap(correlationData[question.id]);
  }

  // Allow wordcloud rendering even for non-SA types when explicitly requested
  if (
    chartHint === 'wordcloud' &&
    Array.isArray(summary.labels) &&
    Array.isArray(summary.counts)
  ) {
    const wc: Summary = {
      question: summary.question,
      n: summary.n,
      words: summary.labels.slice(),
      weights: (summary.counts as number[]).slice(),
    } as Summary;
    return renderWordCloud(wc);
  }

  const barOrientation = getBarOrientation(chartHint);
  const canPie = shouldRenderPie(question.type as string, summary, chartHint);

  if (question.type === 'numeric' && summary.values && summary.values.length) {
    const hint = (chartHint || '').toLowerCase();
    if (hint === 'box' || hint === 'boxplot') {
      const opts: any = {};
      if (question.id === 'A27') { opts.min = 0; opts.max = 100; }
      return renderBoxPlotChartJS(summary, opts);
    }
    return renderNumericSummary(summary, { hideStats: opts?.hideStats });
  }

  if (question.type === 'wordcloud' && summary.words && summary.weights) {
    return renderWordCloud(summary);
  }

  if (question.type === 'longform' && summary.responses && summary.responses.length) {
    if (question.id === 'B128') return renderCategorizedLongform(summary);
    return renderLongform(summary);
  }

  if (summary.labels && summary.counts) {
    if (canPie) {
      return renderPieChart(summary);
    }
    return renderBarChart(summary, barOrientation);
  }

  return null;
}

function renderGroupedCard(
  question: Question,
  summary: Summary,
  options?: { hideTitle?: boolean; hideResponses?: boolean; hideStats?: boolean }
): HTMLElement {
  const card = document.createElement('div');
  card.className = 'chart-card';

  if (!options?.hideTitle) {
    const title = document.createElement('h3');
    title.textContent = question.question;
    card.appendChild(title);
  }

  if (!options?.hideResponses) {
    const responseCount = document.createElement('div');
    responseCount.className = 'response-count';
    responseCount.textContent = `${summary.n} ${getResponseNote(question)}`;
    card.appendChild(responseCount);
  }

  const chartHint = question.chart ?? summary.chart;
  let content: HTMLElement | null = null;
  try {
    content = renderChartContent(question, summary, chartHint, { hideStats: options?.hideStats });
  } catch (err) {
    const warning = document.createElement('div');
    warning.className = 'empty-state';
    warning.textContent = 'Chart unavailable for this item.';
    content = warning;
  }

  const cfg = getCommentary(question.id);
  const commentaryPosition = (cfg?.position ?? (summary as any).commentaryPosition ?? 'above') as 'above' | 'below' | 'left' | 'right';
  const commentaryEl = createCommentaryEl(question.id, summary);

  if (commentaryPosition === 'left' || commentaryPosition === 'right') {
    const composed = wrapWithAside(content, commentaryEl, commentaryPosition);
    if (composed) card.appendChild(composed);
  } else {
    if (commentaryEl && commentaryPosition === 'above') {
      card.appendChild(commentaryEl);
    }
    if (content) {
      card.appendChild(content);
    } else {
      const empty = document.createElement('div');
      empty.className = 'empty-state';
      empty.textContent = 'No aggregated data available for this question yet.';
      card.appendChild(empty);
    }
    if (commentaryEl && commentaryPosition !== 'above') {
      card.appendChild(commentaryEl);
    }
  }

  return card;
}

function renderSlides(): void {
  try {
    destroyCharts();
    slidesEl.innerHTML = '';

    let displayIndex = 0;
    const processed = new Set<string>();
  const questionLookup = new Map(questions.map((q) => [q.id, q]));
  (window as any).__ECE25Summaries = summaries;
    const groupMap = new Map<string, QuestionGroup>();
    GROUP_DEFINITIONS.forEach((group) => {
      group.keys.forEach((key) => groupMap.set(key, group));
      if (group.flowKey) {
        groupMap.set(group.flowKey, group);
      }
    });

    const sectionById = new Map<string, { label: string; slug: string; theme?: string; introHtml?: string }>();
    const explicitMode = SECTIONS.some((s) => Array.isArray(s.ids) && s.ids.length > 0);
    if (explicitMode) {
      // In explicit mode, map all provided ids to their sections
      // and also ensure each section's startId anchors a header so nav links work.
      SECTIONS.filter((s) => !s.htmlOnly).forEach((s) => {
        const expanded = expandIdTokens((s.ids ?? []) as string[], questionLookup);
        expanded.forEach((id) => sectionById.set(id, { label: s.label, slug: s.slug, theme: s.theme, introHtml: s.introHtml }));
        // Fallback: if no ids provided (or startId not included), ensure startId anchors the section header.
        if (s.startId && !sectionById.has(s.startId) && questionLookup.has(s.startId)) {
          sectionById.set(s.startId, { label: s.label, slug: s.slug, theme: s.theme, introHtml: s.introHtml });
        }
      });
    } else {
      SECTIONS.filter((s) => !s.htmlOnly && s.startId).forEach((s) => sectionById.set(s.startId!, { label: s.label, slug: s.slug, theme: s.theme, introHtml: s.introHtml }));
    }
    const renderedSections = new Set<string>();
    let currentSectionInner: HTMLElement | null = null;

    const navEl = document.getElementById('section-nav');
    if (navEl) {
      navEl.innerHTML = '';
      const items: Array<{ slug: string; label: string }> = [];
      if (explicitMode) {
        SECTIONS.forEach((s) => items.push({ slug: s.slug, label: s.label }));
      } else {
        const order: Array<{ index: number; slug: string; label: string }> = [];
        questions.forEach((q, idx) => {
          const sec = sectionById.get(q.id);
          if (sec) {
            order.push({ index: idx, slug: sec.slug, label: sec.label });
          }
        });
        order.sort((a, b) => a.index - b.index);
        order.forEach((o) => items.push({ slug: o.slug, label: o.label }));
      }
      items.forEach((item) => {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.href = `#section-${item.slug}`;
        a.textContent = item.label;
        li.appendChild(a);
        navEl.appendChild(li);
      });

      // Accessible enhancements: scrollspy and focus management (always on)
      const headers = Array.from(document.querySelectorAll<HTMLElement>('.section-header[id]'));
      const links = Array.from(navEl.querySelectorAll<HTMLAnchorElement>('a'));
      const navContainer = navEl.closest('nav.section-nav') as HTMLElement | null;
      const linkMap = new Map<string, HTMLAnchorElement>();
      headers.forEach((h) => {
        const href = `#${h.id}`;
        const link = links.find((l) => l.getAttribute('href') === href);
        if (link) linkMap.set(h.id, link);
      });

      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          const link = linkMap.get(e.target.id);
          if (!link) return;
          if (e.isIntersecting) {
            links.forEach((l) => l.removeAttribute('aria-current'));
            link.setAttribute('aria-current', 'true');
          }
        });
      }, { root: null, rootMargin: '-40% 0% -50% 0%', threshold: 0 });
      headers.forEach((h) => io.observe(h));

      navEl.addEventListener('click', (evt) => {
        const a = (evt.target as HTMLElement).closest('a');
        if (!a) return;
        const id = a.getAttribute('href')?.slice(1);
        const target = id ? document.getElementById(id) : null;
        if (!target) return;
        setTimeout(() => { target.setAttribute('tabindex', '-1'); target.focus({ preventScroll: true }); }, 0);
      });

      // Apply 'is-stuck' only when the nav reaches the top (not before)
      if (navContainer) {
        const initialTop = navContainer.getBoundingClientRect().top + window.scrollY;
        const onScroll = () => {
          if (window.scrollY >= initialTop) navContainer.classList.add('is-stuck');
          else navContainer.classList.remove('is-stuck');
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
      }
    }

    let orderedQuestions: Question[] = questions;
    if (explicitMode) {
      const seen = new Set<string>();
      const list: Question[] = [];
      SECTIONS.forEach((s) => {
        const expanded = expandIdTokens((s.ids ?? []) as string[], questionLookup);
        expanded.forEach((id) => { const q = questionLookup.get(id); if (q && !seen.has(id)) { list.push(q); seen.add(id); } });
      });
      questions.forEach((q) => { if (!seen.has(q.id)) { list.push(q); seen.add(q.id); }});
      orderedQuestions = list;
    }

    // Precompute where each section should appear (first index of any of its ids)
    const firstIndexBySlug = new Map<string, number>();
    SECTIONS.forEach((s) => {
      // Skip htmlOnly sections - they are rendered separately
      if (s.htmlOnly) return;
      const expanded = expandIdTokens((s.ids ?? []) as string[], questionLookup);
      const ids = new Set<string>(expanded);
      // Ensure startId serves as an anchor if present in dataset
      if (s.startId && questionLookup.has(s.startId)) ids.add(s.startId);
      for (let i = 0; i < orderedQuestions.length; i += 1) {
        if (ids.has(orderedQuestions[i].id)) { firstIndexBySlug.set(s.slug, i); break; }
      }
    });

    // Map to track section inner elements by slug for proper slide placement
    const sectionInnerBySlug = new Map<string, HTMLElement>();

    // Helper to render an htmlOnly section
    const renderHtmlOnlySection = (s: typeof SECTIONS[number]) => {
      renderedSections.add(s.slug);
      const block = document.createElement('div');
      const themeClass = s.theme ? ` theme--${s.theme}` : '';
      block.className = `section-block${themeClass}`;
      const inner = document.createElement('div');
      inner.className = 'section-inner';
      const sectionEl = document.createElement('div');
      sectionEl.className = 'section-header';
      sectionEl.id = `section-${s.slug}`;
      sectionEl.innerHTML = `<h2>${s.label}</h2>`;
      inner.appendChild(sectionEl);
      if (s.introHtml) {
        const introSlide = document.createElement('article');
        introSlide.className = 'slide section-intro-slide';
        introSlide.innerHTML = s.introHtml;
        inner.appendChild(introSlide);
      }
      block.appendChild(inner);
      slidesEl.appendChild(block);
      sectionInnerBySlug.set(s.slug, inner);
    };

    // Render leading htmlOnly sections (e.g., Preface) before any questions
    for (const s of SECTIONS) {
      if (!s.htmlOnly) break; // Stop at first non-htmlOnly section
      if (!renderedSections.has(s.slug)) {
        renderHtmlOnlySection(s);
      }
    }

    orderedQuestions.forEach((question, idx) => {
      // If a section is scheduled to start at this index, render its header now.
      SECTIONS.filter((s) => !s.htmlOnly).forEach((s) => {
        const startAt = firstIndexBySlug.get(s.slug);
        if (startAt === idx && !renderedSections.has(s.slug)) {
          renderedSections.add(s.slug);
          const block = document.createElement('div');
          const themeClass = s.theme ? ` theme--${s.theme}` : '';
          block.className = `section-block${themeClass}`;
          const inner = document.createElement('div');
          inner.className = 'section-inner';
          const sectionEl = document.createElement('div');
          sectionEl.className = 'section-header';
          sectionEl.id = `section-${s.slug}`;
          sectionEl.innerHTML = `<h2>${s.label}</h2>`;
          inner.appendChild(sectionEl);
          if (s.introHtml) {
            const introSlide = document.createElement('article');
            introSlide.className = 'slide section-intro-slide';
            introSlide.innerHTML = s.introHtml;
            inner.appendChild(introSlide);
          }
          block.appendChild(inner);
          slidesEl.appendChild(block);
          sectionInnerBySlug.set(s.slug, inner);
          currentSectionInner = inner;
        }
      });

      // Update currentSectionInner based on which section this question belongs to
      const questionSection = sectionById.get(question.id);
      if (questionSection && sectionInnerBySlug.has(questionSection.slug)) {
        currentSectionInner = sectionInnerBySlug.get(questionSection.slug)!;
      }

      if (processed.has(question.id)) {
        return;
      }

      const summary = summaries[question.id];
      const isCorrelation = question.type === 'correlation' && correlationData[question.id];

      // Skip if no summary and not a correlation question
      if (!summary && !isCorrelation) {
        processed.add(question.id);
        return;
      }

      // Legacy per-question trigger retained as a safeguard (no-op if already rendered)
      const section = sectionById.get(question.id);
      if (section && !renderedSections.has(section.slug)) {
        renderedSections.add(section.slug);
        const block = document.createElement('div');
        const themeClass = section.theme ? ` theme--${section.theme}` : '';
        block.className = `section-block${themeClass}`;
        const inner = document.createElement('div');
        inner.className = 'section-inner';
        const sectionEl = document.createElement('div');
        sectionEl.className = 'section-header';
        sectionEl.id = `section-${section.slug}`;
        sectionEl.innerHTML = `<h2>${section.label}</h2>`;
        inner.appendChild(sectionEl);
        if (section.introHtml) {
          const introSlide = document.createElement('article');
          introSlide.className = 'slide section-intro-slide';
          introSlide.innerHTML = section.introHtml;
          inner.appendChild(introSlide);
        }
        block.appendChild(inner);
        slidesEl.appendChild(block);
        currentSectionInner = inner;
      }

      const group = groupMap.get(question.id);
      if (group) {

        const slide = document.createElement('article');
        slide.className = 'slide grouped';

        const header = document.createElement('div');
        header.className = 'slide-header';
        // Compute numbering range for grouped questions
        const keysToRender = group.keys.filter((key) => summaries[key] && !processed.has(key));
        const startNum = displayIndex + 1;
        const endNum = displayIndex + keysToRender.length;
        const aggregate = !!(group as any).aggregateCount;
        // Separate lines: kicker (question number or range) then optional title
        const kicker = document.createElement('span');
        kicker.className = 'slide-kicker';
        if (aggregate && endNum > startNum) {
          kicker.textContent = `Questions ${startNum}–${endNum}`;
        } else {
          kicker.textContent = `Question ${startNum}`;
        }
        header.appendChild(kicker);
        if (group.title && group.title.trim().length > 0) {
          const titleEl = document.createElement('h2');
          titleEl.textContent = group.title;
          header.appendChild(titleEl);
        }
        slide.appendChild(header);

        const hideChildTitles = !!(group as any).hideTitles;
        const showGroupResponses = (group as any).showResponses ?? hideChildTitles;

        // Add response count right after header for all groups
        if (showGroupResponses && keysToRender.length > 0) {
          const mode = ((group as any).responseCountMode === 'sum') ? 'sum' : 'max';
          const ns = keysToRender.map((k) => (summaries[k]?.n || 0));
          const aggN = ns.length ? (mode === 'sum' ? ns.reduce((a, b) => a + b, 0) : Math.max(...ns)) : 0;
          if (aggN > 0) {
            const groupResp = document.createElement('div');
            groupResp.className = 'response-count';
            groupResp.textContent = `${aggN} responses`;
            slide.appendChild(groupResp);
          }
        }

        if (group.flowOnly) {
          slide.classList.add('flow-only');

          group.keys.forEach((key) => processed.add(key));
          if (group.flowKey) {
            processed.add(group.flowKey);
          }

          const flowSummary = group.flowKey ? summaries[group.flowKey] : undefined;
          const flowSection = document.createElement('div');
          flowSection.className = 'flow-section flow-section--full';
          const flowTitle = document.createElement('h3');
          flowTitle.textContent = 'Co-op location transitions across terms';
          flowSection.appendChild(flowTitle);

          if (flowSummary) {
            try {
              flowSection.appendChild(renderSankey(flowSummary, { collapseCoopPrefix: true, colorMode: 'gradient' }));
            } catch (err) {
              const empty = document.createElement('div');
              empty.className = 'empty-state';
              empty.textContent = 'Flow chart unavailable for this group.';
              flowSection.appendChild(empty);
            }
          } else {
            const empty = document.createElement('div');
            empty.className = 'empty-state';
            empty.textContent = 'No flow data available for this summary yet.';
            flowSection.appendChild(empty);
          }

          slide.appendChild(flowSection);
          (currentSectionInner ?? slidesEl).appendChild(slide);
          return;
        }

        let stackedSectionEl: HTMLElement | null = null;
        if (group.stacked) {
          const stackedSection = document.createElement('div');
          stackedSection.className = 'stacked-section';
          const stackedChart = renderStackedSummary(group, questionLookup);
          if (stackedChart) {
            stackedSection.appendChild(stackedChart);
          } else {
            const empty = document.createElement('div');
            empty.className = 'empty-state';
            empty.textContent = 'No aggregated data available for this summary.';
            stackedSection.appendChild(empty);
          }
          slide.appendChild(stackedSection);
          stackedSectionEl = stackedSection;
        }

        let rendered = 0;
        if (!group.stacked && !(group as any).perTermToggle && !(group as any).chipPair) {
          const grid = document.createElement('div');
          grid.className = 'chart-grid';

          group.keys.forEach((key) => {
            let itemQuestion = questionLookup.get(key);
            let itemSummary = summaries[key];
            processed.add(key);

            if (!itemQuestion || !itemSummary) {
              return;
            }

            const card = renderGroupedCard(itemQuestion, itemSummary, { hideTitle: hideChildTitles, hideResponses: (hideChildTitles || showGroupResponses) });
            grid.appendChild(card);
            rendered += 1;
          });

          if (rendered > 0) {
            // Optional group-level commentary from groups.ts
            const groupCommentaryHtml = (group as any).commentaryHtml as string | undefined;
            const groupCommentaryText = (group as any).commentary as string | undefined;
            const groupCommentaryPos = ((group as any).commentaryPosition as ('above'|'below'|'left'|'right')) || 'above';
            let commentaryEl: HTMLElement | null = null;
            if (groupCommentaryHtml || groupCommentaryText) {
              commentaryEl = document.createElement('div');
              commentaryEl.className = 'commentary';
              if (groupCommentaryHtml) commentaryEl.innerHTML = groupCommentaryHtml;
              else commentaryEl.textContent = groupCommentaryText || '';
            }

            if (commentaryEl && (groupCommentaryPos === 'left' || groupCommentaryPos === 'right')) {
              const composed = wrapWithAside(grid, commentaryEl, groupCommentaryPos);
              if (composed) slide.appendChild(composed);
            } else {
              if (commentaryEl && groupCommentaryPos === 'above') {
                slide.appendChild(commentaryEl);
              }
              slide.appendChild(grid);
              if (commentaryEl && groupCommentaryPos !== 'above') {
                slide.appendChild(commentaryEl);
              }
            }
          }
        } else if ((group as any).perTermToggle) {
          const keys = keysToRender.slice();
          const bar = document.createElement('div');
          bar.style.display = 'flex';
          bar.style.gap = '6px';
          bar.style.flexWrap = 'wrap';
          bar.style.margin = '6px 0 8px';

          const panel = document.createElement('div');
          panel.className = 'chart-grid';

          const chipLabels = (group as any).chipLabels as string[] | undefined;

          const cards: HTMLElement[] = [];
          const cardNs: number[] = [];
          keys.forEach((key) => {
            const itemQuestion = questionLookup.get(key);
            const itemSummary = summaries[key];
            processed.add(key);
            if (!itemQuestion || !itemSummary) return;
            const card = renderGroupedCard(itemQuestion, itemSummary, { hideTitle: true, hideResponses: true });
            if (card) { cards.push(card); cardNs.push(itemSummary.n ?? 0); }
          });
          rendered = cards.length;

          const labels = chipLabels?.length
            ? chipLabels
            : keys.map((key, i) => {
                const q = questionLookup.get(key) as any;
                const m = q?.question?.match(/Coop\s*(\d+)/i);
                return m ? `Co-op ${m[1]}` : `Term ${i + 1}`;
              });

          // Per-chip response count shown only when chipLabels is used (counts differ)
          const respCountEl = chipLabels?.length ? document.createElement('div') : null;
          if (respCountEl) respCountEl.className = 'response-count';

          let active = 0;
          function renderActive() {
            panel.innerHTML = '';
            if (cards[active]) panel.appendChild(cards[active]);
            if (respCountEl) respCountEl.textContent = `${cardNs[active] ?? 0} responses`;
            Array.from(bar.children).forEach((btn, i) => {
              (btn as HTMLButtonElement).style.background = i === active ? 'rgba(37,99,235,0.16)' : 'transparent';
              (btn as HTMLButtonElement).style.border = i === active ? '1px solid rgba(37,99,235,0.28)' : '1px solid rgba(148,163,184,0.28)';
            });
          }

          labels.forEach((lab, i) => {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.textContent = lab;
            btn.style.padding = '6px 10px';
            btn.style.borderRadius = '999px';
            btn.style.border = '1px solid rgba(148,163,184,0.28)';
            btn.style.cursor = 'pointer';
            btn.style.background = 'transparent';
            btn.style.color = '#475569';
            btn.addEventListener('click', () => { active = i; renderActive(); });
            bar.appendChild(btn);
          });

          slide.appendChild(bar);
          if (respCountEl) slide.appendChild(respCountEl);
          renderActive();
          slide.appendChild(panel);

          const groupCommentaryHtml = (group as any).commentaryHtml as string | undefined;
          const groupCommentaryText = (group as any).commentary as string | undefined;
          const groupCommentaryPos = ((group as any).commentaryPosition as ('above'|'below'|'left'|'right')) || 'above';
          if (groupCommentaryHtml || groupCommentaryText) {
            const commentaryEl = document.createElement('div');
            commentaryEl.className = 'commentary';
            if (groupCommentaryHtml) commentaryEl.innerHTML = groupCommentaryHtml;
            else commentaryEl.textContent = groupCommentaryText || '';
            if (groupCommentaryPos === 'above') {
              slide.insertBefore(commentaryEl, bar);
            } else {
              slide.appendChild(commentaryEl);
            }
          }

        } else if ((group as any).chipPair) {
          const cp = (group as any).chipPair as { geoLabels: string[]; viewLabels: string[]; keyGroups: string[][] };

          // Mark all keys as processed
          cp.keyGroups.flat().forEach((k) => { processed.add(k); questionLookup.get(k) && processed.add(k); });

          const makeChipBar = (labels: string[], activeIdx: number, onChange: (i: number) => void) => {
            const bar = document.createElement('div');
            bar.style.cssText = 'display:flex;gap:6px;flex-wrap:wrap;margin:0 0 6px';
            labels.forEach((lab, i) => {
              const btn = document.createElement('button');
              btn.type = 'button';
              btn.textContent = lab;
              btn.style.cssText = 'padding:6px 10px;border-radius:999px;cursor:pointer;font-size:0.85rem;color:#475569;';
              const setActive = (active: boolean) => {
                btn.style.background = active ? 'rgba(37,99,235,0.16)' : 'transparent';
                btn.style.border = active ? '1px solid rgba(37,99,235,0.28)' : '1px solid rgba(148,163,184,0.28)';
              };
              setActive(i === activeIdx);
              btn.addEventListener('click', () => { onChange(i); });
              bar.appendChild(btn);
            });
            return bar;
          };

          const panel = document.createElement('div');

          let activeGeo = 0;
          let activeView = 0;

          const refreshChipBars = () => {
            Array.from(geoBar.children).forEach((btn, i) => {
              (btn as HTMLButtonElement).style.background = i === activeGeo ? 'rgba(37,99,235,0.16)' : 'transparent';
              (btn as HTMLButtonElement).style.border = i === activeGeo ? '1px solid rgba(37,99,235,0.28)' : '1px solid rgba(148,163,184,0.28)';
            });
            Array.from(viewBar.children).forEach((btn, i) => {
              (btn as HTMLButtonElement).style.background = i === activeView ? 'rgba(37,99,235,0.16)' : 'transparent';
              (btn as HTMLButtonElement).style.border = i === activeView ? '1px solid rgba(37,99,235,0.28)' : '1px solid rgba(148,163,184,0.28)';
            });
          };

          const renderPanel = () => {
            panel.innerHTML = '';
            const keys = activeView === 0
              ? [cp.keyGroups[activeGeo][0]]
              : cp.keyGroups[activeGeo].slice(1);

            const grid = document.createElement('div');
            grid.className = keys.length > 1 ? 'chart-grid' : '';
            let hasContent = false;
            keys.forEach((key) => {
              const q = questionLookup.get(key);
              const s = summaries[key];
              if (!q || !s) {
                const empty = document.createElement('div');
                empty.className = 'empty-state';
                empty.textContent = 'Data not yet available.';
                grid.appendChild(empty);
                return;
              }
              const card = renderGroupedCard(q, s, { hideTitle: false, hideResponses: false, hideStats: activeView === 1 });
              grid.appendChild(card);
              hasContent = true;
            });
            if (hasContent || keys.length) panel.appendChild(grid);
            refreshChipBars();
          };

          const geoBar = makeChipBar(cp.geoLabels, activeGeo, (i) => { activeGeo = i; renderPanel(); });
          const viewBar = makeChipBar(cp.viewLabels, activeView, (i) => { activeView = i; renderPanel(); });
          geoBar.style.marginTop = '12px';
          viewBar.style.marginTop = '8px';

          slide.appendChild(geoBar);
          slide.appendChild(viewBar);
          renderPanel();
          slide.appendChild(panel);
          rendered = cp.keyGroups.flat().length;

          // Add commentary for chipPair groups
          const groupCommentaryHtml = (group as any).commentaryHtml as string | undefined;
          const groupCommentaryText = (group as any).commentary as string | undefined;
          const groupCommentaryPos = ((group as any).commentaryPosition as ('above'|'below'|'left'|'right')) || 'below';
          if (groupCommentaryHtml || groupCommentaryText) {
            const commentaryEl = document.createElement('div');
            commentaryEl.className = 'commentary';
            if (groupCommentaryHtml) commentaryEl.innerHTML = groupCommentaryHtml;
            else commentaryEl.textContent = groupCommentaryText || '';
            if (groupCommentaryPos === 'above') {
              slide.insertBefore(commentaryEl, geoBar);
            } else {
              slide.appendChild(commentaryEl);
            }
          }

        } else {
          // Stacked-only: still support commentary above/below; left/right degrades to below
          const groupCommentaryHtml = (group as any).commentaryHtml as string | undefined;
          const groupCommentaryText = (group as any).commentary as string | undefined;
          const groupCommentaryPos = ((group as any).commentaryPosition as ('above'|'below'|'left'|'right')) || 'above';
          // Mark all child questions as processed to avoid duplicate group rendering
          group.keys.forEach((key) => processed.add(key));
          if (groupCommentaryHtml || groupCommentaryText) {
            const commentaryEl = document.createElement('div');
            commentaryEl.className = 'commentary';
            if (groupCommentaryHtml) commentaryEl.innerHTML = groupCommentaryHtml;
            else commentaryEl.textContent = groupCommentaryText || '';
            if (groupCommentaryPos === 'above' && stackedSectionEl) {
              slide.insertBefore(commentaryEl, stackedSectionEl);
            } else {
              slide.appendChild(commentaryEl);
            }
          }
        }

        if (group.flowKey) {
          const flowSummary = summaries[group.flowKey];
          if (flowSummary && flowSummary.nodes && flowSummary.links) {
            processed.add(group.flowKey);
            const flowSection = document.createElement('div');
            flowSection.className = 'flow-section';
            const flowTitle = document.createElement('h3');
            flowTitle.textContent = 'Transitions between co-op terms';
            flowSection.appendChild(flowTitle);
            try {
              flowSection.appendChild(renderSankey(flowSummary, { collapseCoopPrefix: true, colorMode: 'gradient' }));
            } catch (err) {
              const empty = document.createElement('div');
              empty.className = 'empty-state';
              empty.textContent = 'Flow chart unavailable for this group.';
              flowSection.appendChild(empty);
            }
            slide.appendChild(flowSection);
          }
        }

        if (rendered > 0 || group.stacked || group.flowKey) {
          (currentSectionInner ?? slidesEl).appendChild(slide);
          // Advance display index: either as a group (1) or aggregate (all children)
          displayIndex += aggregate ? Math.max(1, keysToRender.length) : 1;
        }

        return;
      }

      processed.add(question.id);
      displayIndex += 1;

      const slide = document.createElement('article');
      slide.className = 'slide';

      const header = document.createElement('div');
      header.className = 'slide-header';

      // Skip question enumeration for correlation slides
      if (question.type !== 'correlation') {
        const kicker = document.createElement('span');
        kicker.className = 'slide-kicker';
        kicker.textContent = `Question ${displayIndex}`;
        header.appendChild(kicker);
      }

      const title = document.createElement('h2');
      title.textContent = question.question;
      header.appendChild(title);

      const tags = document.createElement('div');
      tags.className = 'slide-tags';

      if (question.type === 'MC') {
        tags.appendChild(createPill('Multiple Choice', 'pill-mc'));
      } else if (question.type === 'MS') {
        tags.appendChild(createPill('Multi-select', 'pill-ms'));
      } else if (question.type === 'numeric') {
        tags.appendChild(createPill('Numeric', 'pill-numeric'));
      } else if (question.type === 'wordcloud') {
        tags.appendChild(createPill('Short Answer', 'pill-freeform'));
      } else if (question.type === 'longform') {
        tags.appendChild(createPill('Long Answer', 'pill-freeform'));
      } else if (question.type === 'correlation') {
        tags.appendChild(createPill('Correlation', 'pill-correlation'));
      }

      if (tags.childElementCount) {
        header.appendChild(tags);
      }

      slide.appendChild(header);

      if (summary && !isCorrelation) {
        const responseCount = document.createElement('div');
        responseCount.className = 'response-count';
        responseCount.textContent = `${summary.n} ${getResponseNote(question)}`;
        slide.appendChild(responseCount);
      }

      // Handle correlation questions specially
      if (isCorrelation) {
        const correlationEntry = correlationData[question.id];
        
        // Show commentary if available
        if (correlationEntry.commentary) {
          const commentaryEl = document.createElement('div');
          commentaryEl.className = 'commentary';
          commentaryEl.textContent = correlationEntry.commentary;
          slide.appendChild(commentaryEl);
        }

        // Render heatmap
        let content: HTMLElement | null = null;
        try {
          content = renderCorrelationHeatmap(correlationEntry);
        } catch (err) {
          const warning = document.createElement('div');
          warning.className = 'empty-state';
          warning.textContent = 'Heatmap unavailable for this correlation.';
          content = warning;
        }
        
        if (content) {
          slide.appendChild(content);
        }

        (currentSectionInner ?? slidesEl).appendChild(slide);
        return;
      }

      const highlight = createHighlight(question, summary!);
      if (highlight) {
        slide.appendChild(highlight);
      }

      const cfg = getCommentary(question.id);
      const commentaryPosition = (cfg?.position ?? (summary as any).commentaryPosition ?? 'above') as 'above' | 'below' | 'left' | 'right';
      const commentaryEl = createCommentaryEl(question.id, summary!);

      const chartHint = question.chart ?? summary!.chart;
      let content: HTMLElement | null = null;
      try {
        content = renderChartContent(question, summary!, chartHint);
      } catch (err) {
        const warning = document.createElement('div');
        warning.className = 'empty-state';
        warning.textContent = 'Chart unavailable for this question.';
        content = warning;
      }

      if (commentaryPosition === 'left' || commentaryPosition === 'right') {
        const composed = wrapWithAside(content, commentaryEl, commentaryPosition);
        if (composed) slide.appendChild(composed);
      } else {
        if (commentaryEl && commentaryPosition === 'above') {
          slide.appendChild(commentaryEl);
        }
        if (content) {
          slide.appendChild(content);
        } else {
          const empty = document.createElement('div');
          empty.className = 'empty-state';
          empty.textContent = 'No aggregated data available for this question yet.';
          slide.appendChild(empty);
        }
        if (commentaryEl && commentaryPosition !== 'above') {
          slide.appendChild(commentaryEl);
        }
      }

      (currentSectionInner ?? slidesEl).appendChild(slide);
    });

    // Render trailing htmlOnly sections (e.g., Acknowledgements) after all questions
    SECTIONS.filter((s) => s.htmlOnly && !renderedSections.has(s.slug)).forEach((s) => {
      renderHtmlOnlySection(s);
    });
  } catch (error) {
    destroyCharts();
    const message = error instanceof Error ? error.message : 'Failed to render charts.';
    slidesEl.innerHTML = `<div class="empty-state">${message}</div>`;
  }
}

function updateIntroStats(): void {
  statQuestionsEl.textContent = questions.length.toString();
}

async function loadData(): Promise<void> {
  const summaryRes = await fetch(RESPONSES_URL);

  if (!summaryRes.ok) {
    throw new Error('Failed to load data file.');
  }

  type SummaryPayload = {
    question: string;
    n: number;
    labels?: string[];
    counts?: Array<number | string>;
    values?: Array<number | string>;
    median?: number | string;
    words?: string[];
    weights?: Array<number | string>;
    responses?: string[];
    chart?: string;
    ordered?: boolean;
    order?: string[];
    nodes?: string[];
    links?: Array<{ source: string; target: string; value: number }>;
    flow?: boolean;
    commentary?: string;
    commentaryPosition?: string;
    histogram_number_prefix?: string;
  };

  const summaryData = (await summaryRes.json()) as Record<string, SummaryPayload>;

  const processed = Object.entries(summaryData).map(([id, payload]) => {
    const counts = normalizeNumberArray(payload.counts);
    const values = normalizeNumberArray(payload.values);
    const median = normalizeNumber(payload.median);
    const weights = normalizeNumberArray(payload.weights);

    const entry: Summary = {
      question: payload.question,
      n: payload.n,
    };

    if (payload.labels && counts) {
      entry.labels = payload.labels;
      entry.counts = counts;
    }

    if (values) {
      entry.values = values;
    }

    if (median !== undefined) {
      entry.median = median;
    }

    if (payload.words && weights) {
      entry.words = payload.words;
      entry.weights = weights;
    }

    if (payload.responses) {
      entry.responses = payload.responses;
    }

    if (typeof payload.chart === 'string' && payload.chart.toLowerCase() !== 'none') {
      entry.chart = payload.chart;
    }

    if (typeof payload.ordered === 'boolean') {
      entry.ordered = payload.ordered;
    }

    if (Array.isArray(payload.order)) {
      entry.order = payload.order;
    }

    if (Array.isArray(payload.nodes)) {
      entry.nodes = payload.nodes;
    }

    if (Array.isArray(payload.links)) {
      entry.links = payload.links.map((item) => ({
        source: item.source,
        target: item.target,
        value: item.value,
      }));
    }

    if (typeof payload.flow === 'boolean') {
      entry.flow = payload.flow;
    }

    if (typeof payload.commentary === 'string' && payload.commentary.trim()) {
      entry.commentary = payload.commentary;
    }
    if (typeof payload.commentaryPosition === 'string') {
      const pos = payload.commentaryPosition.toLowerCase();
      if (pos === 'above' || pos === 'below' || pos === 'left' || pos === 'right') {
        entry.commentaryPosition = pos as 'above' | 'below' | 'left' | 'right';
      }
    }

    if (typeof payload.histogram_number_prefix === 'string') {
      entry.histogram_number_prefix = payload.histogram_number_prefix;
    }

    return [id, entry] as [string, Summary];
  });

  summaries = Object.fromEntries(processed);

  // Detect correlation entries (have top_data and bottom_data) and separate them
  const correlationPattern = /^C\d+$/;
  const correlationIds = new Set<string>();
  
  Object.entries(summaryData).forEach(([id, payload]) => {
    if (correlationPattern.test(id) && (payload as any).top_data && (payload as any).bottom_data) {
      correlationIds.add(id);
      correlationData[id] = {
        question: payload.question,
        main_axis: (payload as any).main_axis || '',
        top_data: (payload as any).top_data,
        bottom_data: (payload as any).bottom_data,
        commentary: (payload as any).commentary,
      };
    }
  });

  // Build questions list, excluding correlation entries from normal processing
  questions = processed
    .filter(([id]) => !correlationIds.has(id))
    .map(([id, entry]) => ({
      id,
      question: entry.question,
      type: inferQuestionType(entry) as any,
      chart: entry.chart,
    }))
    .filter((question) => question.type !== 'skip' && !HIDDEN_QUESTION_IDS.has(question.id))
    .sort((a, b) => compareQuestionIds(a.id, b.id));

  // Add correlation entries as questions so they appear in the correlations section
  const correlationQuestions: Question[] = Object.entries(correlationData).map(([id, entry]) => ({
    id,
    question: entry.question,
    type: 'correlation' as any,
    chart: 'heatmap',
  }));

  // Append correlation questions to the main questions list
  questions = [...questions, ...correlationQuestions];
}

function scrollToHash(): void {
  const hash = window.location.hash.slice(1);
  if (hash) {
    const element = document.getElementById(hash);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }
}

function init(): void {
  loadData()
    .then(() => {
      renderSlides();
      updateIntroStats();
      // Handle initial hash on page load
      setTimeout(scrollToHash, 100);
    })
    .catch((error: Error) => {
      slidesEl.innerHTML = `<div class="empty-state">${error.message}</div>`;
    });

  // Listen for hash changes
  window.addEventListener('hashchange', scrollToHash);
}

init();
